import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import PageContainer from "../pages/PageContainer";
import {
  PortfolioRowsSkeleton,
  PortfolioSummarySkeleton,
} from "../Skeleton/PortfolioSkeleton";
import useDelayedFlag from "../Skeleton/useDelayedFlag";
import { useOutletContext, useParams } from "react-router-dom";
import {
  cancelOrderApi,
  getOrder,
  getGuideSetting,
  fetchPortfolioMetrics,
} from "../../api/user";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import {
  faEdit,
  faEllipsisV,
  faTrash,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteOrderModal from "../Modals/deleteOrderModal";
import MobileEditOrderSheet from "./MobileEditOrderSheet";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "d3-format";
import GameSideBar from "./GameSideBar";
import PortfolioGuideSteps from "../guide/PortfolioGuideSteps";
import { LuBriefcase, LuClipboardList, LuReceipt } from "react-icons/lu";
import { baseSymbol, instrumentName } from "../../utils/instrumentFormat";
import { assetClass, priceDecimals } from "../../utils/orderFormat";

function formatNumber1(num, asset_type = "") {
  num = parseFloat(num);

  if (isNaN(num)) return "0";

  if (asset_type.toLowerCase() === "forex") {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
    }).format(num);
  }

  if (Number.isInteger(num)) {
    return new Intl.NumberFormat("en-US").format(num);
  } else {
    const decimalPlaces = (num.toString().split(".")[1] || []).length;

    if (decimalPlaces === 1) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } else {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(num);
    }
  }
}

function formatNumber(num, asset_type = "") {
  num = parseFloat(num);

  if (isNaN(num)) return "0";

  const isForex = asset_type.toLowerCase() === "forex";

  num = parseFloat(num.toFixed(10));

  if (Number.isInteger(num)) {
    return new Intl.NumberFormat("en-US").format(num);
  }

  // Case: num >= 1
  if (num >= 1) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  // Case: Forex AND num < 1
  if (isForex) {
    let rounded = parseFloat(num.toFixed(4));
    if (rounded === 0) {
      rounded = parseFloat(num.toFixed(5));
      if (rounded === 0) {
        rounded = parseFloat(num.toFixed(6));
      }
    }

    const decimalLength = rounded.toString().split(".")[1]?.length || 0;

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalLength,
      maximumFractionDigits: decimalLength,
    }).format(rounded);
  }

  // Case: non-forex and < 1
  const decimalPlaces = (num.toString().split(".")[1] || []).length;

  if (decimalPlaces === 1) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } else {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(num);
  }
}

// When embedded (e.g. inside the public profile tabs) the component renders
// without its own PageContainer so it doesn't double the sidebar offset.
const SectionShell = ({ embedded, gamePage, children }) =>
  embedded ? (
    <>{children}</>
  ) : (
    <PageContainer gamePage={gamePage}>{children}</PageContainer>
  );

const NewPortfolioSection = forwardRef((props, ref) => {
  const game_id = props.gameId ?? null;
  const slug = props.slug ?? null;
  const user_id = props.user_id ?? null;
  const isSelfProfile = props.isSelfProfile ?? true;
  const ignoreDesign = props.ignoreDesign ?? false;
  const disableInternalGuide = props.disableInternalGuide ?? false;
  // `?tab=` opens the portfolio on a specific tab.
  //
  // Client, on trade notifications: a fill that opened or added to a position
  // lands on Holdings, a fill that fully closed one lands on Trade History. The
  // notification carries the answer; this is what reads it, and it is read HERE
  // rather than in each host so the main portfolio and the in-league portfolio
  // cannot end up honouring it differently.
  //
  // An explicit `initialTab` prop still wins — the profile screens pass one and
  // are not driven by the URL. An unrecognised value is ignored rather than
  // trusted, so a hand-edited link cannot put the screen in a state that has no
  // tab.
  const TAB_KEYS = ["holdings", "openTrades", "closedTrades"];
  // `window.location` rather than the router's — this runs above the
  // `useLocation()` call further down, and it is only ever read once, to seed
  // the tab's initial state.
  const tabFromQuery = new URLSearchParams(window.location.search || "").get("tab");
  const initialTab =
    props.initialTab ??
    (TAB_KEYS.includes(tabFromQuery) ? tabFromQuery : "holdings");
  const hideTabBar = props.hideTabBar ?? false;
  const embedded = props.embedded ?? false;
  const showSummary = props.showSummary ?? !embedded;
  const forceLegacy = props.forceLegacy ?? false;
  const applyLogic = props.applyLogic ?? false;

  const navigate = useNavigate();
  const isMobileView = document.body.clientWidth <= 768;
  const [mobileEditTrade, setMobileEditTrade] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [closedWinRate, setClosedWinRate] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [splitsByTicker, setSplitsByTicker] = useState({});
  const [activeButtons, setActiveButtons] = useState(initialTab);

  // Expose setActiveButtons through ref for portfolio guide tab switching
  useImperativeHandle(ref, () => (tabKey) => {
    setActiveButtons(tabKey);
  });

  const [ids, setIds] = useState(0);
  const [loader, setLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const outletContext = useOutletContext();
  const sidebarToggle = Array.isArray(outletContext)
    ? outletContext[0]
    : outletContext?.sidebarToggle;
  const [dropdownStates, setDropdownStates] = useState(
    openTrades.map(() => false),
  );
  const [showNonGroups, setShowNonGroups] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [freeCoin, setFreeCoin] = useState("");
  const [PortfolioCoin, setPortfolioCoin] = useState("");
  const location = useLocation();
  const { traderProfile } = location.state || {};
  const { userId } = useParams();
  const isIdPresent = !!userId;

  const viewedUserId = userId || user_id;
  const loggedInUserId =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("user_id")
      : null;
  const isOwnerView =
    !viewedUserId ||
    (loggedInUserId != null && String(viewedUserId) === String(loggedInUserId));
  const [guideIcon, setGuideIcon] = useState(false);
  const [showChartGuide, setShowChartGuide] = useState(false);
  const [guideButtonPosition, setGuideButtonPosition] = useState(() => {
    const isMobile = window.innerWidth <= 768;
    return {
      bottom: isMobile ? 80 : 16,
      right: 16,
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [hasDraggedSignificantly, setHasDraggedSignificantly] = useState(false);
  const [portfolioMetricsLoading, setPortfolioMetricsLoading] = useState(false);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [metricsFailed, setMetricsFailed] = useState(false);
  const toggleOffAllDropdowns = () => {
    setDropdownStates(openTrades.map(() => false));
  };
  useEffect(() => {
    if (activeButtons === "holdings") {
      fetchOrder(3);
    } else if (activeButtons === "closedTrades") {
      fetchOrder(2);
    } else if (activeButtons === "openTrades") {
      fetchOrder(1);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "g") {
        event.preventDefault();
        setShowChartGuide(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // `quiet` mirrors fetchOrder's: the 30-second background refresh must not
  // put the totals back into their loading state while the user is reading them.
  const refreshPortfolioMetrics = async (quiet = false) => {
    const targetUserId = userId || user_id || null;
    const payload = {};

    if (targetUserId) payload.user_id = targetUserId;
    if (game_id) payload.game_id = game_id;

    try {
      if (!quiet) setPortfolioMetricsLoading(true);
      setMetricsFailed(false);
      const response = await fetchPortfolioMetrics(payload);

      if (response?.data?.status === 200) {
        const { free_cash, portfolio_coin, performance } =
          response.data.data || {};
        if (typeof free_cash !== "undefined") setFreeCoin(free_cash);
        if (typeof portfolio_coin !== "undefined")
          setPortfolioCoin(portfolio_coin);
        setPortfolioSummary(performance ?? null);
      }
    } catch (error) {
      console.error("Error fetching portfolio metrics:", error);
      setMetricsFailed(true);
    } finally {
      if (!quiet) setPortfolioMetricsLoading(false);
    }
  };

  const handleOpenTradesClick = async () => {
    setActiveButtons("openTrades");
    setButtonDropdownState(null);
    toggleOffAllDropdowns();
    await fetchOrder(1);
  };

  const toggleDropdown = (index) => {
    const newDropdownStates = [...dropdownStates];
    newDropdownStates[index] = !newDropdownStates[index];
    setDropdownStates(newDropdownStates);
  };

  const handleClosedTradesClick = async () => {
    setActiveButtons("closedTrades");
    toggleOffAllDropdowns();
    setButtonDropdownState(null);
    await fetchOrder(2);
  };

  const [expandedGroups, setExpandedGroups] = useState({});

  const getOrderTypeLabel = (orderType) => {
    switch (orderType) {
      case 1:
        return "buy";
      case 2:
        return "sell";
      case 3:
        return "short sell";
      case 4:
        return "long position";
      case 5:
        return "buy";
      default:
        return "UNKNOWN";
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });

    setDragStartPosition({ x: clientX, y: clientY });
    setHasDraggedSignificantly(false);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const dragDistance = Math.sqrt(
      Math.pow(clientX - dragStartPosition.x, 2) +
        Math.pow(clientY - dragStartPosition.y, 2),
    );

    if (dragDistance > 10) {
      setHasDraggedSignificantly(true);
    }

    const newRight = window.innerWidth - clientX - dragOffset.x;
    const newBottom = window.innerHeight - clientY - dragOffset.y;

    const clampedRight = Math.max(
      8,
      Math.min(newRight, window.innerWidth - 48),
    );
    const clampedBottom = Math.max(
      8,
      Math.min(newBottom, window.innerHeight - 48),
    );

    setGuideButtonPosition({ right: clampedRight, bottom: clampedBottom });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setTimeout(() => setHasDraggedSignificantly(false), 100);
  };

  useEffect(() => {
    if (!isDragging) return;

    const mouseMoveHandler = (event) => handleDragMove(event);
    const touchMoveHandler = (event) => handleDragMove(event);

    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", touchMoveHandler, {
      passive: false,
    });
    document.addEventListener("touchend", handleDragEnd);

    return () => {
      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", touchMoveHandler);
      document.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  useEffect(() => {
    const fetchGuideStatus = async () => {
      try {
        const response = await getGuideSetting();
        if (response?.data?.success && response?.data?.status === 200) {
          setGuideIcon(response.data.show_icon);
        }
      } catch (error) {
        console.log("Error fetching guide status:", error);
      }
    };

    fetchGuideStatus();
  }, []);

  const calculateGroupPnL = (trades) => {
    let buyCoverTotal = 0;
    let sellShortTotal = 0;
    let openQty = 0;
    let closedQty = 0;

    trades.forEach((trade) => {
      const price = Number(trade.order_price) || 0;
      const qty = Number(trade.order_quantity) || 0;
      const covered = Number(trade.covered_quantity) || 0;

      if (trade.order_type === 1) {
        buyCoverTotal += covered * price;
        openQty += qty - covered;
        closedQty += covered;
      } else if (trade.order_type === 3) {
        sellShortTotal += covered * price;
        openQty += qty - covered;
        closedQty += covered;
      } else if (trade.order_type === 2) {
        sellShortTotal += qty * price;
      } else if (trade.order_type === 5) {
        buyCoverTotal += qty * price;
      }
    });

    const pnl = buyCoverTotal - sellShortTotal;

    let status = "neutral";
    if (closedQty > 0) {
      if (buyCoverTotal < sellShortTotal) {
        status = "profit";
      } else if (buyCoverTotal > sellShortTotal) {
        status = "loss";
      }
    }

    return { pnl, status, isOpen: openQty > 0, hasClosed: closedQty > 0 };
  };

  // Group trades by group_id
  const groupedTrades = useMemo(() => {
    return closedTrades.reduce((acc, trade) => {
      if (trade?.row_type === "dividend") {
        const divKey = `dividend_${trade?.payout_id || trade?.dividend_id || Math.random()}`;
        acc[divKey] = [trade];
      } else {
        const key = trade?.group_id ?? "ungrouped";
        if (!acc[key]) acc[key] = [];
        acc[key].push(trade);
      }
      return acc;
    }, {});
  }, [closedTrades]);

  const handleHoldingsTradesClick = async () => {
    setButtonDropdownState(null);
    setActiveButtons("holdings");
    toggleOffAllDropdowns();
    await fetchOrder(3);
  };

  // `quiet` is for the background refresh below: it re-reads the same endpoint
  // without raising the loader, so a live price update never flashes the
  // skeleton over a list the user is reading.
  const fetchOrder = async (type, quiet = false) => {
    try {
      if (!quiet) setLoader(true);
      const effectiveUserId = userId || user_id || null;

      const response = effectiveUserId
        ? await getOrder(type, effectiveUserId, game_id)
        : await getOrder(type, null, game_id);

      if (response?.data?.status === 200) {
        const rows = response?.data?.data?.data || [];
        // Per-ticker split history, used to caption a trade whose stored numbers a
        // split has restated. Kept in one place for both tabs.
        setSplitsByTicker(response?.data?.data?.splits || {});

        if (type === 1) {
          setHoldings([]);
          setOpenTrades(rows);
        } else if (type === 2) {
          setClosedTrades(rows);
          setClosedWinRate(response?.data?.data?.win_rate ?? null);
        } else if (type === 3) {
          setHoldings(rows);
          refreshPortfolioMetrics(quiet);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      if (!quiet) setLoader(false);
    }
  };

  // Keep Holdings on the live price instead of the one it loaded with.
  //
  // Client 0014833, 3: "Portfolio 'Current Price' should come from a live price
  // feed. I want this number to match current market information and not lag. I
  // assume that if we fix this, the P&L and Market Value on the same page will
  // also update from the same live data." — and then: "LET'S DO IT".
  //
  // Two things were wrong and this is the second. The SOURCE is fixed on the
  // server: StockController::resolveMark now reads Polygon's `fmv`, the same
  // number the Trade screen's websocket pushes, so Current Price, Market Value
  // and P&L all come off one feed. But this screen only ever fetched ONCE, on
  // mount — so however live the source, the page still showed whatever it was
  // worth when you opened it.
  //
  // Re-reading the backend rather than opening a websocket per held symbol is
  // deliberate: the backend is where P&L and Market Value are computed, so the
  // row and the totals move together. A per-symbol socket would update the price
  // on screen while the totals kept the old one — the exact class of
  // disagreement this ticket is about.
  //
  // THE 30 SECONDS IS OURS, not the client's. Each tick is one request that
  // fans out to the user's own tickers, so it is picked to feel live without
  // putting a portfolio screen on the same footing as a chart. The visibility
  // check stops a backgrounded tab polling the data provider for nothing.
  useEffect(() => {
    if (activeButtons !== "holdings") return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchOrder(3, true);
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeButtons]);

  const formatCreatedAt = (createdAt) => {
    const currentTimeUTC = createdAt ? createdAt : moment.utc().format();
    const easternTimeZone = "America/New_York";
    const localTime = moment
      .utc(currentTimeUTC)
      .tz(easternTimeZone)
      .format("MMMM DD, YYYY hh:mm A");
    return localTime;
  };
  const formatCreatedAtHolding = (createdAt) => {
    const currentTimeUTC = createdAt ? createdAt : moment.utc().format();
    const easternTimeZone = "America/New_York";
    const localTime = moment
      .utc(currentTimeUTC)
      .tz(easternTimeZone)
      .format("MMMM DD, YYYY");
    return localTime;
  };

  const formatDateToEastern = (dateStr, options = {}) => {
    if (!dateStr) return "N/A";
    const easternTimeZone = "America/New_York";
    const m = moment.utc(dateStr).tz(easternTimeZone);

    const {
      monthFormat = "long",
      includeYear = true,
      includeTime = true,
    } = options;

    let format = monthFormat === "short" ? "MMM" : "MMMM";
    format += " D";
    if (includeYear) format += ", YYYY";
    if (includeTime) format += " h:mm A";

    return m.format(format);
  };

  const handleCancelOrder = async (id) => {
    setIds(id);
    setOpenModal(true);
  };

  // Tapping an asset anywhere in the portfolio — a holding, an open order, a
  // closed trade — opens its chart.
  //
  // Client: "When a user has holdings in the league portfolio and clicks an
  // asset, it takes them out of league play and to the main site home screen.
  // When a user clicks on an asset from their league portfolio, it should keep
  // them in the league and pull up the asset in the league trade screen. Same
  // function as the main site, but it stays in the league."
  //
  // The league branch used to build `/<slug>/chartSearch/<symbol>`, and THAT
  // ROUTE DOES NOT EXIST — the only chartSearch route in App.jsx is the main
  // site's `/chartSearch/:symbol`. So the URL fell through to the catch-all
  // (`<Route path="*" element={<Navigate to="/feed" replace />} />`) and dumped
  // the member on the main feed, which is exactly what the client described.
  //
  // A league's chart is its Trade screen. Writing the ticker and sending them to
  // `/<slug>/trade` is the same thing this file already does in four other
  // places (`navigate(game_id ? `/${slug}/trade` : "/charts")`) and the same
  // thing a league watchlist row does — see utils/... useLeagueAssetGate. The
  // Trade screen reads `ticker` from localStorage during render, so the asset is
  // already on screen by the time the chart first paints.
  //
  // The main-site branch is untouched: no slug, same `/chartSearch/<symbol>`.
  const handleAssetClick = (stockSymbol) => {
    localStorage.setItem("ticker", stockSymbol);
    navigate(slug ? `/${slug}/trade` : `/chartSearch/${stockSymbol}`);
  };

  const handleEditClick = async (trade, type = null) => {
    console.log("trade >>> ", trade, "editType >>> ", type);

    setButtonDropdownState(null);
    localStorage.setItem("trade", JSON.stringify(trade));

    if (
      activeButtons === "holdings" &&
      type !== "stopLoss" &&
      type !== "newStopLoss"
    ) {
      const holdingForForm = trade?.stock_symbol
        ? {
            stock_symbol: trade.stock_symbol,
            isHolding: true,
            order_quantity: trade?.order_quantity,
            order_type: trade?.order_type,
          }
        : null;
      if (isMobileView) {
        // Mobile: open the bottom sheet directly on this page.
        setMobileEditTrade(holdingForForm);
      } else {
        // Desktop: open /charts with the right-panel prefilled.
        if (trade?.stock_symbol) {
          localStorage.setItem(
            "ticker",
            String(trade.stock_symbol).toUpperCase(),
          );
        }
        if (holdingForForm) {
          localStorage.setItem("editOrder", JSON.stringify(holdingForForm));
        } else {
          localStorage.removeItem("editOrder");
        }
        navigate(game_id ? `/${slug}/trade` : "/charts");
      }
    } else if (activeButtons === "holdings" && type === "newStopLoss") {
      const holdingForForm = trade?.stock_symbol
        ? {
            stock_symbol: trade.stock_symbol,
            isHolding: true,
            order_quantity: trade?.order_quantity,
            order_type: trade?.order_type,
            defaultStopLoss: true,
          }
        : null;
      if (isMobileView) {
        setMobileEditTrade(holdingForForm);
      } else {
        if (trade?.stock_symbol) {
          localStorage.setItem(
            "ticker",
            String(trade.stock_symbol).toUpperCase(),
          );
        }
        if (holdingForForm) {
          localStorage.setItem("editOrder", JSON.stringify(holdingForForm));
        } else {
          localStorage.removeItem("editOrder");
        }
        navigate(game_id ? `/${slug}/trade` : "/charts");
      }
    } else {
      if (isMobileView) {
        setMobileEditTrade(trade);
      } else {
        localStorage.setItem("editOrder", JSON.stringify(trade));
        if (trade?.stock_symbol) {
          localStorage.setItem(
            "ticker",
            String(trade.stock_symbol).toUpperCase(),
          );
        }
        navigate(game_id ? `/${slug}/trade` : "/charts");
      }
    }
  };

  const handleDeleteOrder = async () => {
    const res = await cancelOrderApi(ids);
    if (res?.data?.status === 200) {
      setOpenModal(false);
      if (activeButtons === "openTrades") {
        fetchOrder(1);
      } else {
        fetchOrder(3);
      }
      toast.success("Order canceled.");
    } else {
      toast.error("Could not cancel the order. Check its status and try again if it is still open.");
    }
  };

  const handleDeleteCancel = () => {
    setOpenModal(false);
  };

  const formattedCurrency = format("$,.2f")(1234567.89);
  const customFormattedCurrency = format("$,.4s")(1234567.89);
  const [buttonDropdownState, setButtonDropdownState] = useState(null);

  const toggleButtonDropdown = (currentIndex) => {
    if (buttonDropdownState === currentIndex) {
      setButtonDropdownState(null);
    } else {
      setButtonDropdownState(currentIndex);
    }
  };

  const calculateWinRate = (groups) => {
    let closedGroups = 0;
    let profitableGroups = 0;

    Object.entries(groups)
      .filter(
        ([groupId]) => groupId && groupId !== "null" && groupId !== "undefined",
      )
      // A dividend row has no buys or sells, so 0 === 0 would count it as a
      // completed trade. Dividends must not touch win rate.
      .filter(([, trades]) => trades[0]?.row_type !== "dividend")
      .forEach(([groupId, trades]) => {
        const totalBuyQty = trades
          .filter((t) => t.order_type === 1 || t.order_type === 5)
          .reduce((sum, t) => sum + parseFloat(t.order_quantity), 0);

        const totalSellQty = trades
          .filter((t) => t.order_type === 2 || t.order_type === 3)
          .reduce((sum, t) => sum + parseFloat(t.order_quantity), 0);

        const isCompleted = totalBuyQty === totalSellQty;

        if (isCompleted) {
          closedGroups++;
          const { status } = calculateGroupPnL(trades);
          if (status === "profit") {
            profitableGroups++;
          }
        }
      });

    if (closedGroups === 0) return 0;
    return (profitableGroups / closedGroups) * 100;
  };

  const [expandedHoldings, setExpandedHoldings] = useState({});
  const toggleHolding = (key) =>
    setExpandedHoldings((prev) => ({ ...prev, [key]: !prev[key] }));

  const usd = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const moneyInt = (n) =>
    `$${Math.round(Number(n || 0)).toLocaleString("en-US")}`;
  const qtyFmt = (q) => Number(q || 0).toLocaleString("en-US");
  const usdPrice = (n, symbol) => {
    const dp = priceDecimals(assetClass(symbol), n);
    return `$${Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })}`;
  };

  const stripCompanyName = (trade) =>
    instrumentName({ name: trade?.company_name, symbol: trade?.stock_symbol });

  // order_type: 1/5 = buy, 2 = sell, 3 = short (a short opens with a sell).
  const directionWord = (orderType) =>
    orderType === 1 || orderType === 5 ? "Buy" : "Sell";
  const positionLabel = (orderType) => (orderType === 3 ? "Short" : "Long");
  // postion_type (sic): 1 = market, 2 = limit, 3 = stop. Stored at creation.
  const mechanismWord = (positionType) =>
    positionType === 3 ? "stop" : positionType === 2 ? "limit" : "market";

  const fillDate = (d) => formatDateToEastern(d, { monthFormat: "short" });

  // Trade History row for a dividend payout. Client spec (0014471):
  //   NVDA
  //   Dividend                                   $750.00
  //   3,000 shares × $0.25/share · Oct 1, 2026
  // "Dividend" sits where Long/Short does — no pill. No plus sign (green already
  // means positive), whole shares, no time on the date (it's a dividend, not a
  // trade), no chevron. The rate keeps up to 4 decimals only when the dividend
  // has them (VOO pays $1.8226), so shares × rate still adds up to the amount.
  const renderDividendRow = (row, key, { chevronSpacer = false } = {}) => {
    const symbol = row?.ticker || "DIV";
    const shares = Number(row?.eligible_shares || 0).toLocaleString("en-US", {
      maximumFractionDigits: 4,
    });
    const rate = `$${Number(row?.per_share_amount || 0).toLocaleString(
      "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 4 },
    )}/share`;
    const paidDate = row?.paid_at
      ? formatDateToEastern(row.paid_at, {
          monthFormat: "short",
          includeTime: false,
        })
      : "";

    return (
      <div
        key={key}
        className="bg-white py-4 border-b border-gray-200 md:mb-3 md:rounded-xl md:border md:border-gray-200 md:p-5"
      >
        <div className="flex items-start">
          {renderAvatar(symbol, () => handleAssetClick(symbol))}
          <div className="flex-1 ml-3 md:ml-4 min-w-0">
            <p className="font-semibold md:font-bold text-[15px] md:text-[18px] text-[#003434] leading-snug break-words">
              {symbol}
            </p>
            <p className="text-[12px] md:text-[14px] text-[#6F7978] mt-1">
              Dividend
            </p>
            <p className="text-[12px] md:text-[14px] text-[#6F7978] mt-1">
              {shares} shares × {rate}
              {paidDate && (
                <span className="text-[#9a9a9a]"> · {paidDate}</span>
              )}
            </p>
          </div>
          <div className="flex items-start shrink-0 ml-2">
            <p
              className="text-[14px] md:text-[16px] font-semibold text-right whitespace-nowrap"
              style={{ color: "#35b23c" }}
            >
              {usd(row?.total_amount)}
            </p>
            {/* Keeps the amount aligned with trade rows, which carry a chevron here. */}
            {chevronSpacer && (
              <span className="w-6 ml-2 shrink-0" aria-hidden="true" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const tradeTime = (o) => o?.trade_time ?? o?.action_time ?? o?.created_at;
  const tradeTimeMs = (o) => {
    const t = new Date(tradeTime(o)).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const splitsAfterOpen = (symbol, openedAtMs) => {
    const all = splitsByTicker?.[symbol];
    if (!Array.isArray(all) || !all.length || !openedAtMs) return [];
    return all.filter((s) => {
      const execMs = new Date(`${s.execution_date}T00:00:00Z`).getTime();
      return !Number.isNaN(execMs) && execMs >= openedAtMs;
    });
  };

  // "1-for-15 reverse split applied" / "…, 2 splits applied" when more than one.
  const splitNote = (splits) => {
    if (!splits.length) return null;
    if (splits.length === 1) {
      const s = splits[0];
      return `${s.ratio_label}${s.reverse ? " reverse" : ""} split applied`;
    }
    return `${splits.length} splits applied (${splits
      .map((s) => s.ratio_label)
      .join(", ")})`;
  };

  const renderSplitNote = (symbol, openedAtMs) => {
    const note = splitNote(splitsAfterOpen(symbol, openedAtMs));
    if (!note) return null;
    return (
      <>
        <span className="text-gray-300">·</span>
        <span
          className="text-[11px] md:text-[12px] font-medium text-[#6F7978]"
          title="A stock split restated the share count and price stored for this trade. The realized amount is unchanged."
        >
          {note}
        </span>
      </>
    );
  };

  // For a holding, pnl_amount is the current market value (total) and
  // executed_amount is the total cost basis. Gain direction flips for shorts.
  const holdingPnl = (trade) => {
    const current = parseFloat(trade?.pnl_amount) || 0;
    const cost = parseFloat(trade?.executed_amount) || 0;
    const diff = current - cost;
    return trade?.order_type === 3 ? -diff : diff;
  };
  const pnlColor = (value) =>
    value > 0 ? "#35b23c" : value < 0 ? "#e23b3b" : "#8a8b8f";
  const pnlText = (value) => `${value < 0 ? "-" : ""}${usd(Math.abs(value))}`;

  // Individual fills for a holding/group. Uses the real `details` array when
  // present; otherwise synthesizes one fill from the row's own values.
  const getFills = (trade) => {
    const details = Array.isArray(trade?.details) ? trade.details : [];
    if (details.length) {
      return [...details].sort((a, b) => tradeTimeMs(a) - tradeTimeMs(b));
    }
    return [
      {
        order_type: trade?.order_type,
        order_quantity: trade?.order_quantity,
        order_price: trade?.excuted_rate ?? trade?.order_price,
        trade_time: tradeTime(trade),
      },
    ];
  };

  // Fills for a closed-trade round trip: flatten each group row's own fills,
  // oldest first (opening fills then closing fills follow naturally by time).
  const getGroupFills = (group) =>
    group
      .flatMap((t) =>
        Array.isArray(t?.details) && t.details.length ? t.details : [t],
      )
      .slice()
      .sort((a, b) => tradeTimeMs(a) - tradeTimeMs(b));

  const isFilledOrder = (o) =>
    o?.matched_market_price != null ||
    o?.order_status === 3 ||
    o?.order_status === 4;
  const visibleOpenTrades = applyLogic
    ? openTrades.filter((o) => !isFilledOrder(o))
    : openTrades;

  const renderAvatar = (symbol, onClick) => {
    const chip = baseSymbol(symbol);
    return (
      <div
        onClick={onClick}
        style={{
          backgroundColor: "#004c50",
          cursor: onClick ? "pointer" : "default",
        }}
        className="shrink-0 h-11 w-11 rounded-full flex items-center justify-center overflow-hidden"
      >
        <span
          className="text-[11px] font-semibold leading-none px-0.5 text-center break-all"
          style={{ color: "#ffffff" }}
        >
          {chip}
        </span>
      </div>
    );
  };

  const renderChevron = (open) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      style={{
        transform: open ? "rotate(180deg)" : "none",
        transition: "transform .2s",
      }}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="#004c50"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const renderFillRow = (f, idx) => {
    const fillPrice =
      f?.matched_market_price != null && f.matched_market_price !== ""
        ? f.matched_market_price
        : f.order_price;
    return (
      <div key={idx} className="flex items-start justify-between py-1.5">
        <p className="text-[12px] md:text-[14px] leading-snug text-[#1a1a1a] md:text-[#9CA3AF] font-semibold">
          <span className="text-[#003434] ">{directionWord(f.order_type)}</span>{" "}
          <span className="">
            {qtyFmt(f.order_quantity)} @ {usdPrice(fillPrice, f?.stock_symbol)}
          </span>
        </p>
        <p className="text-[12px] text-[#6F7978] whitespace-nowrap pl-4">
          {fillDate(tradeTime(f))}
        </p>
      </div>
    );
  };

  const renderEmptyState = ({ Icon, title, body, cta }) => (
    <div className="flex flex-col items-center justify-center text-center pt-6 pb-16 px-6">
      <span className="w-14 h-14 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
        <Icon className="text-[#9CA3AF] w-6 h-6" />
      </span>
      <p className="text-lg font-bold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 mt-1 max-w-[320px] [text-wrap:balance]">
        {body}
      </p>
      {cta && (
        <button
          type="button"
          onClick={() => navigate(slug ? `/${slug}/trade` : "/charts")}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primaryColor text-white font-semibold font-poppins"
        >
          {cta}
        </button>
      )}
    </div>
  );

  const activeTabHasRows =
    activeButtons === "openTrades"
      ? openTrades.length > 0
      : activeButtons === "holdings"
        ? holdings.length > 0
        : closedTrades.length > 0;
  const showRowSkeleton = useDelayedFlag(loader && !activeTabHasRows);

  return (
    <>
      <SectionShell
        embedded={embedded}
        gamePage={game_id && true && !ignoreDesign}
      >
        {!embedded && slug && (
          <GameSideBar slug={slug} activeMenu={user_id ? "" : "portfolio"} />
        )}
        <div className={embedded ? "min-h-screen" : ""}>
          <div className="bg-white">
            {!forceLegacy ? (
              <main
                className={`h-full bg-white ${embedded ? "" : "md:pt-[33px]"}`}
              >
                <div
                  className={`tabular-nums pt-4 pb-24 px-3 md:pt-8 md:pb-12 md:px-8 md:rounded-2xl md:border max-w-4xl w-full mx-auto ${
                    embedded ? "md:border-gray-100" : "md:border-gray-200"
                  }`}
                >
                  {showSummary &&
                    !metricsFailed &&
                    freeCoin === "" &&
                    PortfolioCoin === "" && <PortfolioSummarySkeleton />}
                  {showSummary && (freeCoin !== "" || PortfolioCoin !== "") && (
                    <div
                      id="portfolio-guide-summary"
                      className="rounded-xl mb-6 p-4 md:px-8 md:py-5 md:mb-6 flex items-stretch border border-[#BFC8C8]/30 shadow-sm"
                      style={{ backgroundColor: "rgba(53,178,60,0.03)" }}
                    >
                      <div className="flex-1 pr-3 md:pr-8">
                        <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
                          Free Cash
                        </p>
                        <p
                          className="mt-1 text-[20px] md:text-[24px] font-semibold leading-tight"
                          style={{ color: "#004c50" }}
                        >
                          {usd(freeCoin)}
                        </p>
                      </div>
                      <div
                        className="w-px self-stretch my-1"
                        style={{ backgroundColor: "rgba(0,76,80,0.05)" }}
                      />
                      <div className="flex-1 pl-3 md:pl-8 text-right">
                        <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
                          Portfolio
                        </p>
                        <p
                          className="mt-1 text-[20px] md:text-[24px] font-semibold leading-tight"
                          style={{ color: "#004c50" }}
                        >
                          {usd(PortfolioCoin)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tab pills */}
                  {!hideTabBar && (
                    <div className="flex gap-2 mb-6 md:justify-center">
                      {[
                        {
                          key: "openTrades",
                          label: "Open Orders",
                          onClick: handleOpenTradesClick,
                          id: "portfolio-guide-open-orders",
                        },
                        {
                          key: "holdings",
                          label: "Holdings",
                          onClick: handleHoldingsTradesClick,
                          id: "portfolio-guide-current-holdings",
                        },
                        {
                          key: "closedTrades",
                          label: "Trade History",
                          onClick: handleClosedTradesClick,
                          id: "portfolio-guide-closed-trades",
                        },
                      ].map((pill) => (
                        <button
                          key={pill.key}
                          id={pill.id}
                          onClick={pill.onClick}
                          className={`flex-1 md:flex-none md:px-6 py-2.5 rounded-xl text-[14px] font-medium transition-colors shadow-sm ${
                            activeButtons === pill.key
                              ? "text-white"
                              : "bg-white text-[#3F4848] border border-[#BFC8C8]/30"
                          }`}
                          style={
                            activeButtons === pill.key
                              ? { backgroundColor: "#003434" }
                              : {}
                          }
                        >
                          {pill.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ===================== OPEN ORDERS ===================== */}
                  {activeButtons === "openTrades" &&
                    (visibleOpenTrades.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between px-4 md:px-5 pb-2 mb-[18px] border-b border-[#E0E6ED]">
                          <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978] ml-[56px] md:ml-[60px]">
                            Asset
                          </span>
                          <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
                            Order Value
                          </span>
                        </div>
                        {visibleOpenTrades.map((trade, index) => {
                          const showActions =
                            !userId &&
                            trade?.id != null &&
                            isSelfProfile === true;
                          const orderValue =
                            (parseFloat(trade?.order_price) || 0) *
                            (parseFloat(trade?.order_quantity) || 0);
                          return (
                            <div
                              key={index}
                              className="bg-white py-4 border-b border-gray-200 md:mb-3 md:rounded-xl md:border md:border-gray-200 md:p-5"
                            >
                              <div className="flex items-start">
                                {renderAvatar(trade?.stock_symbol, () =>
                                  handleAssetClick(trade?.stock_symbol),
                                )}
                                <div className="flex-1 ml-3 md:ml-4 min-w-0">
                                  <div className="flex items-start">
                                    <div className="flex-1 min-w-0 pr-3">
                                      <p className="font-semibold md:font-bold text-[16px] md:text-[18px] text-[#003434] leading-snug break-words">
                                        {stripCompanyName(trade)}
                                      </p>
                                      <p className="text-[12px] md:text-sm font-semibold mt-1">
                                        <span className="text-[#6F7978]">
                                          {directionWord(trade?.order_type)}
                                        </span>{" "}
                                        <span className="text-[#6F7978]">
                                          {mechanismWord(trade?.postion_type)}
                                        </span>
                                      </p>
                                      <p className="text-[14px] md:text-base font-semibold text-[#1a1a1a] md:text-[#0d2e2c] mt-1.5">
                                        {qtyFmt(trade?.order_quantity)} @{" "}
                                        {usdPrice(
                                          trade?.order_price,
                                          trade?.stock_symbol,
                                        )}
                                      </p>
                                    </div>
                                    <p className="shrink-0 text-[13px] md:text-[16px] md:font-semibold text-[#3F4848] md:text-[#0d2e2c] text-right whitespace-nowrap">
                                      {moneyInt(orderValue)}
                                    </p>
                                  </div>
                                  {showActions && (
                                    <div className="border-t border-dashed border-gray-200 mt-3 pt-2.5 md:pt-3 flex items-center gap-3">
                                      <button
                                        onClick={() => handleEditClick(trade)}
                                        className="text-xs md:text-[14px] font-medium text-[#006565]"
                                      >
                                        Modify
                                      </button>
                                      <span className="text-gray-300">·</span>
                                      <button
                                        onClick={() =>
                                          handleCancelOrder(trade?.id)
                                        }
                                        className="text-xs md:text-[14px] font-medium text-[#006565]"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : showRowSkeleton ? (
                      <PortfolioRowsSkeleton valueLabel="Order Value" />
                    ) : (
                      !loader &&
                      isOwnerView &&
                      renderEmptyState({
                        Icon: LuClipboardList,
                        title: "No open orders",
                        body: (
                          <>
                            {"Pending orders wait here until they fill"}
                            <br />
                            {"or you cancel them."}
                          </>
                        ),
                        cta: "Place an Order",
                      })
                    ))}

                  {/* ===================== HOLDINGS ===================== */}
                  {activeButtons === "holdings" &&
                    (holdings.length > 0 ? (
                      holdings.map((trade, index) => {
                        const key = index;
                        const expanded = !!expandedHoldings[key];
                        const showActions =
                          !isIdPresent && isSelfProfile === true;
                        const current = parseFloat(trade?.pnl_amount) || 0;
                        const qty = parseFloat(trade?.order_quantity) || 0;
                        const currentPrice = qty ? current / qty : 0;
                        const marketValue = current;
                        const costBasis = parseFloat(trade?.excuted_rate) || 0;
                        const pnl = holdingPnl(trade);
                        const fills = getFills(trade);
                        return (
                          <div
                            key={index}
                            className="bg-white py-4 border-b border-gray-200 md:mb-3 md:rounded-xl md:border md:border-gray-200 md:p-5"
                          >
                            <div className="flex items-start">
                              {renderAvatar(trade?.stock_symbol, () =>
                                handleAssetClick(trade?.stock_symbol),
                              )}
                              <div className="flex-1 ml-3 md:ml-4 min-w-0">
                                <p className="font-semibold md:font-bold text-[15px] md:text-[18px] text-[#003434] leading-snug break-words">
                                  {stripCompanyName(trade)}
                                </p>
                                <p className="text-[12px] md:text-[14px] text-[#6B7280] mt-1 flex items-center gap-2 flex-wrap">
                                  <span>
                                    {positionLabel(trade?.order_type)}
                                  </span>
                                  {renderSplitNote(
                                    trade?.stock_symbol,
                                    tradeTimeMs(fills[0] || trade),
                                  )}
                                </p>
                              </div>
                              <button
                                onClick={() => toggleHolding(key)}
                                className="shrink-0 ml-2 p-1"
                                aria-label="Toggle fills"
                              >
                                {renderChevron(expanded)}
                              </button>
                            </div>

                            {/* metrics — 2x2 on mobile, single 1x4 row on desktop */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4 mt-3 md:mt-4 md:pl-[60px]">
                              <div>
                                <p className="text-xs  uppercase tracking-wide text-[#6F7978] font-medium">
                                  Current Price
                                </p>
                                <p className="text-[15px] md:text-[18px] font-semibold text-[#003434] mt-0.5">
                                  {trade?.pnl_amount != null ? (
                                    usdPrice(currentPrice, trade?.stock_symbol)
                                  ) : (
                                    <span className="text-[#9a9a9a]">—</span>
                                  )}
                                </p>
                              </div>
                              <div className="text-right md:text-left">
                                <p className="text-xs  uppercase tracking-wide text-[#6F7978] font-medium">
                                  P&amp;L
                                </p>
                                <p
                                  className="text-[15px] md:text-[16px] font-semibold mt-0.5"
                                  style={{
                                    color:
                                      trade?.pnl_amount != null &&
                                      trade?.executed_amount != null
                                        ? pnlColor(pnl)
                                        : "#9a9a9a",
                                  }}
                                >
                                  {trade?.pnl_amount != null &&
                                  trade?.executed_amount != null
                                    ? pnlText(pnl)
                                    : "—"}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs  uppercase tracking-wide text-[#6F7978] font-medium">
                                  Cost Basis
                                </p>
                                <p className="text-[15px] md:text-[18px] font-semibold text-[#003434] mt-0.5">
                                  {trade?.excuted_rate != null ? (
                                    usdPrice(costBasis, trade?.stock_symbol)
                                  ) : (
                                    <span className="text-[#9a9a9a]">—</span>
                                  )}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs  uppercase tracking-wide text-[#6F7978] font-medium">
                                  Market Value
                                </p>
                                <p className="text-[15px] md:text-[18px] font-semibold text-[#003434] mt-0.5">
                                  {trade?.pnl_amount != null ? (
                                    moneyInt(marketValue)
                                  ) : (
                                    <span className="text-[#9a9a9a]">—</span>
                                  )}
                                </p>
                              </div>
                            </div>

                            {/* FILLED (expanded) */}
                            {expanded && (
                              <div className="mt-4 md:pl-[60px]">
                                <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-[#BFC8C8] font-semibold mb-1">
                                  Filled
                                </p>
                                {fills.map((f, i) => renderFillRow(f, i))}
                              </div>
                            )}

                            {/* action links */}
                            {showActions && (
                              <div className="border-t border-dashed border-gray-200 mt-3 pt-2.5 md:pt-3 md:ml-[60px] flex items-center gap-3">
                                {trade?.stop_loss_details &&
                                trade.stop_loss_details.length > 0 ? (
                                  // Active stop-loss state: "Stop Loss $7.85"
                                  // (label + price neutral grey) · Edit · Close
                                  // Position (both primary teal, equal weight).
                                  <>
                                    <span className="text-[13px] md:text-[14px] font-medium text-[#6F7978]">
                                      Stop Loss{" "}
                                      {usdPrice(
                                        trade.stop_loss_details[0].order_price,
                                        trade?.stock_symbol,
                                      )}
                                    </span>
                                    <span className="text-gray-300">·</span>
                                    <button
                                      onClick={() =>
                                        handleEditClick(
                                          trade.stop_loss_details[0],
                                          "stopLoss",
                                        )
                                      }
                                      className="text-[13px] md:text-[14px] font-medium"
                                      style={{ color: "#006565" }}
                                    >
                                      Edit
                                    </button>
                                    <span className="text-gray-300">·</span>
                                    <button
                                      onClick={() => handleEditClick(trade)}
                                      className="text-[13px] md:text-[14px] font-medium"
                                      style={{ color: "#006565" }}
                                    >
                                      Close Position
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleEditClick(trade, "newStopLoss")
                                      }
                                      className="text-[13px] md:text-[14px] font-medium"
                                      style={{ color: "#006565" }}
                                    >
                                      Add Stop Loss
                                    </button>
                                    <span className="text-gray-300">·</span>
                                    <button
                                      onClick={() => handleEditClick(trade)}
                                      className="text-[13px] md:text-[14px] font-medium"
                                      style={{ color: "#006565" }}
                                    >
                                      Close Position
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : showRowSkeleton ? (
                      <PortfolioRowsSkeleton />
                    ) : (
                      !loader &&
                      renderEmptyState(
                        isSelfProfile
                          ? {
                              Icon: LuBriefcase,
                              title: "No open positions",
                              body: (
                                <>
                                  {"You're in cash."}
                                  <br />
                                  {"Open a position and it lands here."}
                                </>
                              ),
                              cta: "Place a Trade",
                            }
                          : {
                              Icon: LuBriefcase,
                              title: "No open positions",
                              body: "This trader has no open positions right now.",
                            },
                      )
                    ))}

                  {/* ===================== TRADE HISTORY ===================== */}
                  {activeButtons === "closedTrades" &&
                    (Object.keys(groupedTrades).length > 0 ? (
                      <>
                        <div className="flex items-center justify-between px-4 md:px-5 pb-2 mb-1 border-b border-[#E0E6ED]">
                          <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978] ml-[56px] md:ml-[60px]">
                            Asset
                          </span>
                          <div className="flex items-center">
                            <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
                              Realized P&amp;L
                            </span>
                            {/* matches the cards' chevron column so the label's
                                  right edge lines up with the P&L value, not the chevron */}
                            <span
                              className="w-6 ml-2 shrink-0"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        {Object.entries(groupedTrades).map(
                          ([groupId, group]) => {
                            const expanded = !!expandedGroups[groupId];
                            const head = group[0] || {};

                            if (head?.row_type === "dividend") {
                              return renderDividendRow(head, groupId, {
                                chevronSpacer: true,
                              });
                            }

                            const fills = getGroupFills(group);
                            const opener = fills[0] || head;
                            const groupPnL = calculateGroupPnL(group);
                            // Partial fills stay "Open" until fully closed; the
                            // realized P&L number shows once anything has closed
                            // (a dash only while nothing has been realized yet).
                            const isOpen = groupPnL.isOpen;
                            const hasClosed = groupPnL.hasClosed;
                            const realized = -groupPnL.pnl;
                            // Single-fill open: the lone fill sits on the card
                            // surface — no chevron, no FILLED header, no divider.
                            // Multi-fill open and all closed trades get a chevron
                            // with the fills in an expandable FILLED section.
                            const singleFillOpen = isOpen && fills.length <= 1;
                            const hasChevron = !singleFillOpen;
                            return (
                              <div
                                key={groupId}
                                className="bg-white py-4 border-b border-gray-200 md:mb-3 md:rounded-xl md:border md:border-gray-200 md:p-5"
                              >
                                <div className="flex items-start">
                                  {renderAvatar(head?.stock_symbol, () =>
                                    handleAssetClick(head?.stock_symbol),
                                  )}
                                  <div className="flex-1 ml-3 md:ml-4 min-w-0">
                                    <p className="font-semibold md:font-bold text-[15px] md:text-[18px] text-[#003434] leading-snug break-words">
                                      {stripCompanyName(head)}
                                    </p>
                                    <p className="text-[12px] md:text-[14px] text-[#6F7978] mt-1 flex items-center gap-2 flex-wrap">
                                      <span>
                                        {positionLabel(opener?.order_type)}
                                      </span>
                                      {isOpen && (
                                        <span
                                          className="px-1.5 py-0.5 rounded text-[10px] md:text-[11px] font-semibold uppercase tracking-wide"
                                          style={{
                                            backgroundColor: "#eaeef0",
                                            color: "#004c50",
                                          }}
                                        >
                                          Open
                                        </span>
                                      )}
                                      {renderSplitNote(
                                        head?.stock_symbol,
                                        tradeTimeMs(opener),
                                      )}
                                    </p>
                                  </div>
                                  <div className="flex items-start shrink-0 ml-2">
                                    {hasClosed ? (
                                      <p
                                        className="text-[14px] md:text-[15px] font-semibold text-right whitespace-nowrap"
                                        style={{ color: pnlColor(realized) }}
                                      >
                                        {pnlText(realized)}
                                      </p>
                                    ) : (
                                      <span className="text-[14px] md:text-[15px] font-semibold text-[#b5b5b5] text-right">
                                        –
                                      </span>
                                    )}
                                    {/* Fixed chevron column, reserved even when there's
                                          no chevron (single-fill open), so the value/dash
                                          always shares one right edge — the dash no longer
                                          drifts into the chevron column. */}
                                    <span className="w-6 ml-2 shrink-0 flex justify-center">
                                      {hasChevron && (
                                        <button
                                          onClick={() => toggleGroup(groupId)}
                                          className="p-0.5"
                                          aria-label="Toggle fills"
                                        >
                                          {renderChevron(expanded)}
                                        </button>
                                      )}
                                    </span>
                                  </div>
                                </div>

                                {/* Single-fill open: fill on the card surface */}
                                {singleFillOpen && (
                                  <div className="mt-3 md:mt-4 md:pl-[60px]">
                                    {fills.map((f, i) => renderFillRow(f, i))}
                                  </div>
                                )}

                                {/* Multi-fill / closed: expandable FILLED section */}
                                {hasChevron && expanded && (
                                  <div className="border-t border-dashed border-gray-200 mt-3 pt-2 md:pt-3 md:ml-[60px]">
                                    <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-[#9a9a9a] font-semibold mb-1">
                                      Filled
                                    </p>
                                    {fills.map((f, i) => renderFillRow(f, i))}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </>
                    ) : showRowSkeleton ? (
                      <PortfolioRowsSkeleton valueLabel="Realized P&L" />
                    ) : (
                      !loader &&
                      renderEmptyState(
                        isSelfProfile
                          ? {
                              Icon: LuReceipt,
                              title: "No trades yet",
                              body: "Every record starts with one fill. Place your first trade, and your history starts here.",
                              cta: "Place a Trade",
                            }
                          : {
                              Icon: LuReceipt,
                              title: "No trades yet",
                              body: "When this trader makes a trade, it shows up here.",
                            },
                      )
                    ))}
                </div>
              </main>
            ) : (
              <main
                className={
                  isMobileView
                    ? "h-full bg-white justify-center"
                    : "h-full bg-white justify-center"
                }
              >
                <div
                  className={
                    isMobileView
                      ? `pt-4 sm:py-3 sm:px-3 rounded-2xl sm:border bg-white sm:border-white-500 max-w-4xl w-full mx-auto ${"mb-24"} `
                      : activeButtons === "holdings"
                        ? `pt-8 pb-2 px-8 rounded-xl border bg-white border-white-500 max-w-4xl w-full mx-auto mb-12`
                        : "p-8 rounded-2xl border bg-white border-white-500 max-w-4xl w-full mx-auto mb-12"
                  }
                >
                  {!hideTabBar && (
                    <div className="flex justify-center space-x-4 mb-4">
                      <button
                        id="portfolio-guide-open-orders"
                        onClick={handleOpenTradesClick}
                        className={`px-4 py-2 rounded focus:outline-none transition-colors ${
                          activeButtons === "openTrades"
                            ? "bg-primaryColor text-white border"
                            : "bg-white text-[#8A8B8F] border"
                        } ${isMobileView && "text-xs"}`}
                      >
                        Open Orders
                        {/* 2 */}
                      </button>
                      <button
                        id="portfolio-guide-current-holdings"
                        onClick={handleHoldingsTradesClick}
                        className={`px-4 py-2 rounded focus:outline-none transition-colors ${
                          activeButtons === "holdings"
                            ? "bg-primaryColor text-white border"
                            : "bg-white text-[#8A8B8F]  border"
                        } ${isMobileView && "text-xs"}`}
                      >
                        Current Holdings{" "}
                      </button>
                      {/* 1 */}
                      <button
                        id="portfolio-guide-closed-trades"
                        onClick={handleClosedTradesClick}
                        className={`px-4 py-2 rounded focus:outline-none transition-colors ${
                          activeButtons === "closedTrades"
                            ? "bg-primaryColor text-white border"
                            : "bg-white text-[#8A8B8F] border"
                        } ${isMobileView && "text-xs"}`}
                      >
                        Closed Trades
                        {/* 3 */}
                      </button>
                    </div>
                  )}

                  {activeButtons === "openTrades" ? (
                    <>
                      {!loader && openTrades.length > 0
                        ? openTrades.map((trade, index) => (
                            <div
                              key={index}
                              className="w-full p-3 mt-4 bg-white rounded shadow-md flex items-center "
                            >
                              {/* <Link to={ slug ? `/${slug}/buysell/${trade?.stock_symbol}` : `/chartSearch/${trade?.stock_symbol}` }> */}
                              <div
                                onClick={() =>
                                  handleAssetClick(trade?.stock_symbol)
                                }
                                style={{ cursor: "pointer" }}
                                className={` max-sm:p-1.5 flex h-[83px] items-center justify-center sm:px-5 rounded-[41px] md:text-[16px] text-[10px] text-center text-white-A700 w-[83px] ${
                                  !trade?.id
                                    ? "bg-primaryColor"
                                    : trade?.order_type === 3 &&
                                        activeButtons === "openTrades" &&
                                        !trade?.id
                                      ? "bg-primaryColor"
                                      : activeButtons === "closedTrades"
                                        ? "bg-primaryColor"
                                        : activeButtons === "holdings"
                                          ? "bg-primaryColor"
                                          : "bg-gray-500"
                                }`}
                              >
                                <span
                                  className={`text-white max-sm:break-all leading-normal ${
                                    isMobileView
                                      ? "text-[11.5px]"
                                      : "text-[13.5px]"
                                  }`}
                                >
                                  {baseSymbol(trade?.stock_symbol)}
                                </span>
                              </div>

                              <div className="pl-3 flex flex-col">
                                {trade?.order_type === 3 ? (
                                  <p className="text-[12px] text-[#888888] font-inter md:mb-[2.2rem] mb-[1.7rem]">
                                    {trade?.order_type === 3
                                      ? "SHORT SELL"
                                      : ""}
                                  </p>
                                ) : (
                                  <p className="text-[12px] text-[#888888] font-inter  md:mb-[2.2rem] mb-[1.7rem]">
                                    {trade?.order_type === 1
                                      ? "BUY"
                                      : trade?.order_type === 5
                                        ? "BUY"
                                        : trade?.order_type === 4
                                          ? "Holdings"
                                          : "SELL"}
                                  </p>
                                )}

                                <p className="md:text-[10.5px] text-[10.5px] leading-relaxed text-[#888888] font-inter">
                                  {formatCreatedAt(trade?.updated_at)}
                                </p>

                                <p className="text-xs leading-3 text-gray-500">
                                  {trade?.action}
                                </p>
                              </div>
                              <div
                                className={
                                  isMobileView
                                    ? "flex flex-col justify-end ml-auto space-y-1"
                                    : "flex flex-col justify-end ml-auto space-y-2"
                                }
                              >
                                <div>
                                  <p
                                    className={`text-end md:text-[11px] text-[11px] font-semibold font-poppins ${
                                      trade?.order_type === 2
                                        ? "text-[#FF4B4B]"
                                        : "text-[#009D00]"
                                    }`}
                                  >
                                    $
                                    {new Intl.NumberFormat("en-US").format(
                                      trade.pnl_amount,
                                    )}
                                    {/* ${trade?.pnl_amount.toLocaleString("en-US")} */}
                                    {/* {trade.pnl_amount % 1 !== 0
                                        ? Number(trade.pnl_amount).toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "USD",
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }
                                          )
                                        : Number(trade.pnl_amount).toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "USD",
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            }
                                          )} */}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-end md:text-[11px] text-[11px] font-semibold  whitespace-nowrap">
                                    <span style={{ color: "#888888" }}>At</span>{" "}
                                    <span style={{ color: "#888888" }}>
                                      {" "}
                                      $
                                      {trade.order_price.toLocaleString(
                                        "en-US",
                                        {
                                          // style: "currency",
                                          // currency: "USD",
                                        },
                                      )}
                                    </span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-end md:text-[11px] text-[11px] text-[#888888] mb-[6px] md:mb-[] ">
                                    (
                                    {(trade?.order_quantity).toLocaleString(
                                      "en-US",
                                    )}
                                    )
                                  </p>
                                </div>
                              </div>
                              {activeButtons === "openTrades" &&
                              !userId &&
                              trade?.id !== null &&
                              isSelfProfile === true ? (
                                <div
                                  className="ml-4"
                                  style={{ position: "relative" }}
                                >
                                  <button
                                    onClick={() => toggleDropdown(index)}
                                    type="button"
                                  >
                                    <FontAwesomeIcon
                                      icon={faEllipsisV}
                                      className=""
                                    />
                                  </button>
                                  {dropdownStates[index] && (
                                    <div className="absolute top-8 text-green-500 left-[-15px] w-10 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 md:mr-[220px] mr-[20px]">
                                      <button
                                        onClick={() =>
                                          handleCancelOrder(trade?.id)
                                        }
                                        className="block text-primaryColor px-4 py-2 text-sm hover:bg-gray-100 hover:text-gray-900"
                                        role="menuitem"
                                      >
                                        <div className="flex flex-row justify-center items-center">
                                          <FontAwesomeIcon icon={faTrash} />
                                        </div>
                                      </button>
                                      <button
                                        onClick={() => handleEditClick(trade)}
                                        className="block px-4 py-2 text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                        role="menuitem"
                                      >
                                        <div className="flex flex-row justify-center items-center">
                                          <FontAwesomeIcon icon={faEdit} />
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ))
                        : !loader && (
                            <div className="text-center font-bold text-black py-4 px-4 mt-4 mb-3">
                              <p>No Data Found</p>
                            </div>
                          )}
                    </>
                  ) : activeButtons === "closedTrades" ? (
                    <>
                      <div className="flex justify-end mt-4">
                        {/* <button
                          onClick={() => setShowNonGroups(!showNonGroups)}
                          className="px-4 py-2 bg-primaryColor text-white rounded-md"
                        >
                          {showNonGroups ? "Back to Groups" : "Non Groups"}
                        </button> */}
                      </div>
                      {!showNonGroups && (
                        <>
                          {Object.entries(groupedTrades)
                            .filter(
                              ([groupId]) =>
                                groupId !== null &&
                                groupId !== "null" &&
                                groupId !== undefined,
                            )
                            .map(([groupId, trades], index) => {
                              console.log(groupId, "groupId is getting me");

                              if (!groupId) return null;

                              // sort by group_sequence
                              const sortedTrades = trades.sort(
                                (a, b) => b.group_sequence - a.group_sequence,
                              );
                              const latestTrade = sortedTrades[0]; // parent trade

                              if (latestTrade?.row_type === "dividend") {
                                return renderDividendRow(latestTrade, groupId);
                              }

                              // Check if all orders in the group are fully covered
                              // Calculate total buy and sell quantities
                              const totalBuyQty = sortedTrades
                                .filter(
                                  (t) =>
                                    t.order_type === 1 || t.order_type === 5,
                                )
                                .reduce(
                                  (sum, t) =>
                                    sum + parseFloat(t.order_quantity),
                                  0,
                                );

                              const totalSellQty = sortedTrades
                                .filter(
                                  (t) =>
                                    t.order_type === 2 || t.order_type === 3,
                                )
                                .reduce(
                                  (sum, t) =>
                                    sum + parseFloat(t.order_quantity),
                                  0,
                                );

                              const isCompleted = totalBuyQty == totalSellQty; // group completed if sell covers buy
                              console.log(groupId, "isCompleted:", isCompleted);
                              // Prefer the backend's wallet-ledger win rate (source
                              // of truth, matches the profile); fall back to the
                              // client calc only if the API didn't return it.
                              const winRate =
                                closedWinRate != null
                                  ? closedWinRate
                                  : calculateWinRate(groupedTrades);

                              return (
                                <>
                                  {index === 0 && (
                                    <div className="p-3 m-2 bg-gray-100 rounded mb-3 text-sm font-semibold">
                                      Win Rate: {Math.round(Number(winRate))}%
                                    </div>
                                  )}

                                  <div className="w-full p-3 mt-4 bg-white rounded shadow-md">
                                    <div className="grid grid-cols-[auto_1fr_auto] items-center sm:gap-4 gap-2">
                                      <div
                                        onClick={() =>
                                          handleAssetClick(
                                            latestTrade?.stock_symbol,
                                          )
                                        }
                                        style={{ cursor: "pointer" }}
                                        className={`mx-auto max-sm:p-1.5 flex sm:h-[83px] items-center justify-center sm:px-5 rounded-[41px] md:text-[16px] text-[10px] text-center text-white-A700 sm:w-[83px] h-14 w-14 ${
                                          activeButtons === "closedTrades"
                                            ? "bg-primaryColor"
                                            : "bg-gray-500"
                                        }`}
                                      >
                                        <span
                                          className={`text-white max-sm:break-all leading-normal ${
                                            isMobileView
                                              ? "text-[11.5px]"
                                              : "text-[13.5px]"
                                          }`}
                                        >
                                          {baseSymbol(
                                            latestTrade?.stock_symbol,
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <h2 className="sm:text-xl text-sm font-montserrat">
                                          {instrumentName({
                                            name: latestTrade?.company_name,
                                            symbol: latestTrade?.stock_symbol,
                                          })}
                                        </h2>

                                        <p className="text-[12px] my-1 text-[#888888] font-inter">
                                          {latestTrade?.order_type === 3
                                            ? "SHORT SELL"
                                            : latestTrade?.order_type === 1
                                              ? "BUY"
                                              : latestTrade?.order_type === 4
                                                ? "LONG POSITION"
                                                : latestTrade?.order_type === 5
                                                  ? "BUY"
                                                  : "SELL"}
                                        </p>
                                        <div className="flex justify-between items-center">
                                          <button
                                            onClick={() => toggleGroup(groupId)}
                                            className="text-blue-500 text-sm text-left"
                                          >
                                            {isCompleted ? (
                                              (() => {
                                                const { status } =
                                                  calculateGroupPnL(
                                                    sortedTrades,
                                                  );

                                                if (status === "profit") {
                                                  return (
                                                    <img
                                                      width={40}
                                                      src="/lightgreen.png"
                                                    />
                                                  );
                                                } else if (status === "loss") {
                                                  return (
                                                    <img
                                                      width={40}
                                                      src="/lightred.png"
                                                    />
                                                  );
                                                } else {
                                                  return (
                                                    // <img
                                                    //   width={40}
                                                    //   src="lightgray.png"
                                                    // />
                                                    <p className="text-sm text-gray-700">
                                                      No Profit/Loss
                                                    </p>
                                                  );
                                                }
                                              })()
                                            ) : sortedTrades.length > 1 ? ( // ✅ only show if more than 1 trade
                                              expandedGroups[groupId] ? (
                                                <img
                                                  width={40}
                                                  src="/darkgreen.png"
                                                />
                                              ) : (
                                                <img
                                                  width={40}
                                                  src="/darkgreen.png"
                                                />
                                              )
                                            ) : (
                                              <p className="sm:text-sm text-xs text-gray-700">
                                                {formatDateToEastern(
                                                  sortedTrades[0]?.updated_at,
                                                  {
                                                    monthFormat: isMobileView
                                                      ? "short"
                                                      : "long",
                                                    includeYear: !isMobileView,
                                                    includeTime: true,
                                                  },
                                                )}
                                              </p>
                                            )}
                                          </button>

                                          <p className="self-center"></p>
                                        </div>
                                      </div>
                                      <div className="flex items-center md:gap-4 gap-2 sm:justify-end max-sm:w-full">
                                        {isCompleted ? (
                                          <div className="text-center">
                                            <div className="flex items-center gap-2">
                                              <img
                                                width={40}
                                                src="/profit-loss.png"
                                                alt="Completed"
                                              />
                                              <p className="text-sm text-[#000]">
                                                realized
                                                <br /> profit/loss
                                              </p>
                                            </div>
                                            {(() => {
                                              const { pnl, status } =
                                                calculateGroupPnL(sortedTrades);
                                              return (
                                                <p
                                                  className={`sm:text-xl text-base ${
                                                    status === "profit"
                                                      ? "text-[#009D00]" // green
                                                      : status === "loss"
                                                        ? "text-[#FF4B4B]" // red
                                                        : "text-gray-500" // neutral
                                                  }`}
                                                >
                                                  ${formatNumber(Math.abs(pnl))}
                                                </p>
                                              );
                                            })()}
                                          </div>
                                        ) : (
                                          <div className="max-sm:w-full grid sm:gap-3 gap-1">
                                            <div className="grid grid-cols-2 sm:gap-5 gap-2">
                                              <div className="text-right sm:text-[14px] text-xs">
                                                Shares
                                              </div>
                                              <div className="sm:sm:text-[14px] text-xs">
                                                {formatNumber(
                                                  latestTrade?.order_quantity,
                                                )}
                                              </div>
                                            </div>

                                            <div className="grid grid-cols-2 sm:gap-5 gap-2 sm:text-[14px] text-xs ">
                                              <div className="text-right sm:text-[14px] text-xs">
                                                Share Price
                                              </div>
                                              <div className="sm:sm:text-[14px] text-xs">
                                                $
                                                {formatNumber(
                                                  latestTrade?.order_price,
                                                )}
                                              </div>
                                            </div>

                                            <div className="grid grid-cols-2 sm:gap-5 gap-2 items-center">
                                              <div className="text-right sm:text-[14px] text-xs">
                                                Total
                                              </div>
                                              <div className="sm:text-[14px] text-xs">
                                                $
                                                {formatNumber(
                                                  latestTrade?.order_quantity *
                                                    latestTrade?.order_price,
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {expandedGroups[groupId] &&
                                      sortedTrades.length > 1 && (
                                        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700 gap-2 grid grid-cols-[1fr_auto] items-center justify-between">
                                          {sortedTrades && (
                                            <div>
                                              {sortedTrades.map((trade) => (
                                                <div
                                                  key={trade.id}
                                                  className="py-1"
                                                >
                                                  <p className="max-sm:text-xs">
                                                    {formatDateToEastern(
                                                      trade?.updated_at,
                                                      {
                                                        monthFormat:
                                                          isMobileView
                                                            ? "short"
                                                            : "long",
                                                        includeYear: false,
                                                        includeTime: true,
                                                      },
                                                    )}{" "}
                                                    {isMobileView ? (
                                                      // Mobile: compact format
                                                      <>
                                                        {trade?.order_type ===
                                                          1 ||
                                                        trade?.order_type === 5
                                                          ? "Buy"
                                                          : trade?.order_type ===
                                                              2
                                                            ? "Sell"
                                                            : trade?.order_type ===
                                                                3
                                                              ? "Short Sell"
                                                              : ""}{" "}
                                                        {formatNumber(
                                                          trade.order_quantity,
                                                        )}{" "}
                                                        @ $
                                                        {formatNumber(
                                                          trade.order_price,
                                                        )}
                                                      </>
                                                    ) : (
                                                      // Desktop: full format
                                                      <>
                                                        {trade?.order_type ===
                                                          1 ||
                                                        trade?.order_type === 5
                                                          ? "Buy order"
                                                          : trade?.order_type ===
                                                              2
                                                            ? "Sell order"
                                                            : trade?.order_type ===
                                                                3
                                                              ? "Short sell order"
                                                              : ""}{" "}
                                                        {formatNumber(
                                                          trade.order_quantity,
                                                        )}{" "}
                                                        shares @ $
                                                        {formatNumber(
                                                          trade.order_price,
                                                        )}{" "}
                                                        ($
                                                        {formatNumber(
                                                          trade.order_quantity *
                                                            trade.order_price,
                                                        )}
                                                        )
                                                      </>
                                                    )}
                                                  </p>
                                                </div>

                                                //         <div
                                                //   key={idx}
                                                //   className="border-b border-gray-200 last:border-0 py-2"
                                                // >
                                                //   {detail.updated_at ? (
                                                //     new Date(detail.updated_at).toLocaleString("en-US", {
                                                //       month: "long",   // July
                                                //       day: "numeric",  // 20
                                                //       hour: "numeric", // 4
                                                //       minute: "2-digit", // 10
                                                //       hour12: true,    // 12-hour format with am/pm
                                                //     })
                                                //   ) : (
                                                //     "-"
                                                //   )}
                                                //   &nbsp;{getOrderTypeLabel(detail.order_type)} {detail.order_quantity} &nbsp;
                                                //   shares @ {trade?.excuted_rate
                                                //     ? `$${formatNumber(
                                                //         trade?.excuted_rate
                                                //       )} ($${formatNumber(
                                                //         trade?.excuted_rate * trade?.order_quantity
                                                //       )})`
                                                //     : "-"}
                                                // </div>
                                              ))}
                                            </div>
                                          )}
                                          {!isCompleted &&
                                            (() => {
                                              const { pnl, status } =
                                                calculateGroupPnL(sortedTrades);

                                              // ⛔ Hide everything if neutral
                                              if (status === "neutral")
                                                return null;

                                              return (
                                                <div className="text-center">
                                                  <div className="flex items-center justify-end gap-2 sm:mb-2">
                                                    <img
                                                      width={40}
                                                      src="/profit-loss.png"
                                                      alt="Completed"
                                                    />
                                                    <p className="sm:text-sm text-xs text-[#000]">
                                                      realized
                                                      <br /> profit/loss
                                                    </p>
                                                  </div>

                                                  <p
                                                    className={`sm:text-xl text-base ${
                                                      status === "profit"
                                                        ? "text-[#009D00]"
                                                        : "text-[#FF4B4B]"
                                                    }`}
                                                  >
                                                    $
                                                    {formatNumber(
                                                      Math.abs(pnl),
                                                    )}
                                                  </p>
                                                </div>
                                              );
                                            })()}
                                        </div>
                                      )}
                                  </div>
                                </>
                              );
                            })}
                        </>
                      )}
                      {showNonGroups && (
                        <>
                          {!loader && closedTrades.length > 0
                            ? closedTrades.map((trade, index) => (
                                <div
                                  key={index}
                                  className="w-full p-3 mt-4 bg-white rounded shadow-md flex items-center"
                                >
                                  {/* <Link to={ slug ? `/${slug}/buysell/${trade?.stock_symbol}` : `/chartSearch/${trade?.stock_symbol}` }> */}
                                  <div
                                    onClick={() =>
                                      handleAssetClick(trade?.stock_symbol)
                                    }
                                    style={{ cursor: "pointer" }}
                                    className={`max-sm:p-1.5 flex h-[83px] items-center justify-center sm:px-5 rounded-[41px] md:text-[16px] text-[10px] text-center text-white-A700 w-[83px] ${
                                      trade?.order_type === 3 &&
                                      activeButtons === "openTrades"
                                        ? "bg-primaryColor"
                                        : activeButtons === "closedTrades"
                                          ? "bg-primaryColor"
                                          : activeButtons === "holdings"
                                            ? "bg-primaryColor"
                                            : "bg-gray-500"
                                    }`}
                                  >
                                    <span
                                      className={`text-white max-sm:break-all leading-normal ${
                                        isMobileView
                                          ? "text-[11.5px]"
                                          : "text-[13.5px]"
                                      }`}
                                    >
                                      {baseSymbol(trade?.stock_symbol)}
                                    </span>
                                  </div>

                                  <div className="pl-3 flex flex-col">
                                    {trade?.order_type === 3 ? (
                                      <p className="md:text-[12px] text-[12px] text-[#888888] font-inter md:mb-[2.2rem] mb-[1.7rem]">
                                        {trade?.order_type === 3
                                          ? "SHORT SELL"
                                          : ""}
                                      </p>
                                    ) : (
                                      <p className="md:text-[12px] text-[12px] text-[#888888] font-inter md:mb-[2.2rem]  mb-[1.7rem]">
                                        {trade?.order_type === 1
                                          ? "BUY"
                                          : trade?.order_type === 2 ||
                                              trade?.order_type === 3
                                            ? "SELL"
                                            : trade?.order_type === 4
                                              ? "HOLDING"
                                              : trade?.order_type === 5
                                                ? "BUY"
                                                : "SELL"}
                                      </p>
                                    )}

                                    <p className="md:text-[10.5px] text-[10.5px] leading-relaxed text-[#888888] font-inter">
                                      {formatCreatedAt(trade?.updated_at)}
                                    </p>

                                    <p className="text-xs leading-3 text-gray-500">
                                      {trade?.action}
                                    </p>
                                  </div>
                                  <div
                                    className={
                                      isMobileView
                                        ? "flex flex-col justify-end ml-auto space-y-1"
                                        : "flex flex-col justify-end ml-auto space-y-2"
                                    }
                                  >
                                    <div>
                                      <p
                                        className={`text-end text-sm  text-[#000] font-poppins ${
                                          trade?.order_type === 2
                                            ? "text-[#FF4B4B]"
                                            : "text-[#009D00]"
                                        }`}
                                      >
                                        ${formatNumber(trade.pnl_amount)}                                      
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-end md:text-[11px] text-sm  text-[#000]  whitespace-nowraps">
                                        <span style={{ color: "#888888" }}>
                                          At
                                        </span>{" "}
                                        <span style={{ color: "#888888" }}>
                                          ${formatNumber(trade?.order_price)}                                          
                                        </span>
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-end md:text-[11px] text-[11px] text-[#888888] mb-[6px]">
                                        ({formatNumber(trade?.order_quantity)})
                                      </p>
                                    </div>
                                  </div>
                                  {activeButtons === "openTrades" &&
                                  trade?.id !== null &&
                                  isSelfProfile === true ? (
                                    <div style={{ position: "relative" }}>
                                      <button
                                        onClick={() => toggleDropdown(index)}
                                        type="button"
                                      >
                                        <FontAwesomeIcon
                                          icon={faEllipsisV}
                                          className=""
                                        />
                                      </button>
                                      {dropdownStates[index] && (
                                        <div className="absolute top-8 text-green-500 left-[-15px] w-10 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 md:mr-[220px] mr-[20px]">
                                          <button
                                            onClick={() =>
                                              handleCancelOrder(trade?.id)
                                            }
                                            className="block text-primaryColor  text-sm hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                          >
                                            <div className="flex flex-row justify-center items-center">
                                              <FontAwesomeIcon icon={faTrash} />
                                            </div>
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleEditClick(trade)
                                            }
                                            className="block  text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                          >
                                            <div className="flex flex-row justify-center items-center">
                                              <FontAwesomeIcon icon={faEdit} />
                                            </div>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              ))
                            : !loader && (
                                <div className="text-center font-bold text-black py-4 px-4 mt-4 mb-3">
                                  <p>No Data Found</p>
                                </div>
                              )}
                        </>
                      )}
                    </>
                  ) : activeButtons === "holdings" ? (
                    <>
                      <div className="flex justify-end mt-4"></div>

                      {!loader && holdings.length === 0 ? (
                        <div className="text-center font-bold text-black py-4 px-4 mt-4 mb-3">
                          <p>No Data Found</p>
                        </div>
                      ) : !showNonGroups && !loader && holdings.length > 0 ? (
                        holdings
                          .filter((trade) => trade.group_id !== null)
                          .map((trade, index) => (
                            <div className="w-full p-3 mt-4 bg-white rounded shadow-md">
                              <div className="grid grid-cols-[auto_1fr_auto] items-center sm:gap-4 gap-2">
                                <div
                                  onClick={() =>
                                    handleAssetClick(trade?.stock_symbol)
                                  }
                                  style={{ cursor: "pointer" }}
                                  className={`max-sm:p-1.5 flex sm:h-[83px] items-center justify-center sm:px-5 rounded-[41px] md:text-[16px] sm:text-[10px] text-center text-white-A700 sm:w-[83px] h-14 w-14 ${
                                    trade?.order_type === 3 &&
                                    activeButtons === "openTrades"
                                      ? "bg-primaryColor"
                                      : activeButtons === "closedTrades"
                                        ? "bg-primaryColor"
                                        : activeButtons === "holdings"
                                          ? "bg-primaryColor"
                                          : "bg-gray-500"
                                  }`}
                                >
                                  <span
                                    className={`text-white max-sm:break-all leading-normal ${
                                      isMobileView
                                        ? "text-[9px]"
                                        : "text-[13.5px]"
                                    }`}
                                  >
                                    {baseSymbol(trade?.stock_symbol)}
                                  </span>
                                </div>
                                <div>
                                  <h2 className="sm:text-xl text-sm font-montserrat max-md:mb-1">
                                    {instrumentName({
                                      name: trade?.company_name,
                                      symbol: trade?.stock_symbol,
                                    })}
                                  </h2>

                                  {!isMobileView && (
                                    <p className="text-[12px] my-1 text-[#888888] font-inter">
                                      {trade?.order_type === 3
                                        ? "SHORT SELL"
                                        : trade?.order_type === 1
                                          ? "BUY"
                                          : trade?.order_type === 4
                                            ? "LONG POSITION"
                                            : trade?.order_type === 5
                                              ? "BUY"
                                              : "SELL"}
                                    </p>
                                  )}
                                  <div className="flex md:gap-4 gap-2 items-center">
                                    <button
                                      className="text-blue-500 text-sm underline text-left"
                                      onClick={() =>
                                        toggleGroup(trade.group_id)
                                      }
                                    >
                                      <img
                                        width={isMobileView ? 25 : 40}
                                        src="/darkgreen.png"
                                      />
                                    </button>
                                    {isMobileView && (
                                      <p className="text-[12px] my-1 text-[#888888] font-inter">
                                        {trade?.order_type === 3
                                          ? "SHORT SELL"
                                          : trade?.order_type === 1
                                            ? "BUY"
                                            : trade?.order_type === 4
                                              ? "LONG POSITION"
                                              : trade?.order_type === 5
                                                ? "BUY"
                                                : "SELL"}
                                      </p>
                                    )}
                                    <p className="self-center sm:text-[14px] text-xs max-md:hidden">
                                      Total shares {trade.order_quantity}
                                    </p>
                                    <p className="self-center"></p>
                                  </div>
                                </div>
                                <div className="grid sm:grid-cols-[1fr_auto] max-md:text-right grid-cols-[1fr_auto] items-center md:gap-4 gap-2  justify-between">
                                  <div>
                                    <div className="md:grid grid-cols-2 md:gap-5 gap-2 md:mb-3 mb-1">
                                      <div className="sm:text-right md:text-[14px] text-sm max-md:hidden">
                                        Current Price
                                      </div>
                                      <div className="text-sm">
                                        {trade?.order_price
                                          ? isMobileView
                                            ? // 👉 Mobile view: only price
                                              `$${formatNumber(
                                                trade?.order_price,
                                              )}`
                                            : // 👉 Desktop view: price + total in brackets
                                              `$${formatNumber(
                                                trade?.order_price,
                                              )} ($${formatNumber(
                                                trade?.order_price *
                                                  trade?.order_quantity,
                                              )})`
                                          : "-"}
                                      </div>
                                    </div>
                                    {!isMobileView && (
                                      <div className="md:grid grid-cols-2 md:gap-5 gap-2  max-md:mb-1">
                                        <div className="sm:text-right md:text-[14px] text-xs max-md:hidden">
                                          Cost Basis
                                        </div>
                                        <div className="text-sm">
                                          {/* {trade?.excuted_rate
                                          ? `$${formatNumber(
                                              trade?.excuted_rate
                                            )} ($${formatNumber(
                                              trade?.excuted_rate *
                                                trade?.order_quantity
                                            )})`
                                          : "-"} */}

                                          {trade?.excuted_rate
                                            ? // 👉 Desktop view: price + total in brackets
                                              `$${formatNumber(
                                                trade?.excuted_rate,
                                              )} ($${formatNumber(
                                                trade?.excuted_rate *
                                                  trade?.order_quantity,
                                              )})`
                                            : "-"}
                                        </div>
                                      </div>
                                    )}
                                    <div className="grid sm:grid-cols-2 grid-cols-[auto_1fr] sm:gap-5 gap-2 items-center">
                                      <div className="sm:text-right flex items-center gap-3 justify-end sm:text-[14px] text-xs">
                                        <img
                                          width={isMobileView ? 25 : 40}
                                          src="/profit-loss.png"
                                          alt="Completed"
                                        />{" "}
                                        {!isMobileView && "Profit/Loss"}
                                      </div>
                                      <div>
                                        <span className="md:text-xl text-sm font-bold">
                                          {trade?.pnl_amount &&
                                          trade?.executed_amount ? (
                                            (() => {
                                              const pnl = parseFloat(
                                                trade?.pnl_amount,
                                              );
                                              const executed = parseFloat(
                                                trade?.executed_amount,
                                              );

                                              // Default: gray for 0 or unknown
                                              let colorClass = "text-gray-500";

                                              if (trade?.order_type === 3) {
                                                // Short sell
                                                colorClass =
                                                  pnl < executed
                                                    ? "text-green-500"
                                                    : "text-red-500";
                                              } else if (
                                                trade?.order_type === 4
                                              ) {
                                                // Holding/cover
                                                colorClass =
                                                  pnl < executed
                                                    ? "text-red-500"
                                                    : "text-green-500";
                                              }

                                              const diff = Math.abs(
                                                pnl - executed,
                                              );

                                              // Format with commas and 2 decimal places
                                              const formattedDiff =
                                                diff.toLocaleString("en-US", {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                });
                                              return (
                                                <span
                                                  className={`md:text-xl text-sm font-bold ${colorClass}`}
                                                >
                                                  ${formattedDiff}
                                                </span>
                                              );
                                            })()
                                          ) : (
                                            <span className="text-gray-500">
                                              -
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {activeButtons === "openTrades" &&
                                  trade?.id !== null &&
                                  isSelfProfile === true ? (
                                    <div style={{ position: "relative" }}>
                                      <button
                                        onClick={() => toggleDropdown(index)}
                                        type="button"
                                      >
                                        <FontAwesomeIcon
                                          icon={faEllipsisV}
                                          className=""
                                        />
                                      </button>
                                      {dropdownStates[index] && (
                                        <div className="absolute top-8 left-0 z-10 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                          <button
                                            onClick={() =>
                                              handleCancelOrder(trade?.id)
                                            }
                                            className="block text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                          >
                                            <div className="flex flex-row justify-center items-center">
                                              <FontAwesomeIcon icon={faTrash} />
                                            </div>
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleEditClick(trade)
                                            }
                                            className="block text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                          >
                                            <div className="flex flex-row justify-center items-center">
                                              <FontAwesomeIcon icon={faEdit} />
                                            </div>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                  {activeButtons === "holdings" &&
                                  !isIdPresent &&
                                  isSelfProfile === true ? (
                                    <div style={{ position: "relative" }}>
                                      <button
                                        onClick={() =>
                                          toggleButtonDropdown(index)
                                        }
                                        type="button"
                                      >
                                        <FontAwesomeIcon
                                          icon={faEllipsisV}
                                          className=""
                                        />
                                      </button>
                                      {buttonDropdownState === index && (
                                        <div className="absolute top-8 text-green-500 left-[-15px] w-10 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 md:mr-[220px] mr-[20px]">
                                          <button
                                            className="block text-primaryColor text-sm hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                            onClick={() =>
                                              handleEditClick(trade)
                                            }
                                          >
                                            <div className="flex flex-row justify-center items-center">
                                              <FontAwesomeIcon
                                                icon={faWallet}
                                              />
                                            </div>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              {expandedGroups[trade.group_id] && (
                                <div className="mt-3 p-2 bg-gray-50 rounded sm:text-sm text-xs text-gray-700">
                                  <div className="grid grid-cols-[1fr_auto] gap-2 mb-2">
                                    <div>
                                      {[...(trade?.details || [])] // clone array safely
                                        .sort(
                                          (a, b) =>
                                            new Date(b.updated_at) -
                                            new Date(a.updated_at), // latest first
                                        )
                                        .map((detail, idx) => {
                                          const dateStr =
                                            formatDateToEastern(
                                              detail.updated_at,
                                              {
                                                monthFormat: isMobileView
                                                  ? "short"
                                                  : "long",
                                                includeYear: false,
                                                includeTime: true,
                                              },
                                            ) || "-";

                                          const orderType = getOrderTypeLabel(
                                            detail.order_type,
                                          );

                                          const priceCompact =
                                            trade?.excuted_rate
                                              ? `$${formatNumber(
                                                  trade?.excuted_rate,
                                                )}`
                                              : "-";

                                          const priceFull = trade?.excuted_rate
                                            ? `$${formatNumber(
                                                trade?.excuted_rate,
                                              )} ($${formatNumber(
                                                trade?.excuted_rate *
                                                  detail?.order_quantity,
                                              )})`
                                            : "-";

                                          return (
                                            <div key={idx} className="py-1">
                                              {isMobileView ? (
                                                // Mobile view → compact format
                                                <>
                                                  {dateStr} {orderType}{" "}
                                                  {detail.order_quantity} @{" "}
                                                  {/* {priceCompact} */}
                                                  {detail.matched_market_price ??
                                                    detail.order_price}
                                                </>
                                              ) : (
                                                // Desktop view → full format
                                                <>
                                                  {/* @ {priceFull} */}
                                                  {dateStr}&nbsp;
                                                  {orderType}{" "}
                                                  {detail.order_quantity} shares
                                                  @{" "}
                                                  {detail.matched_market_price ??
                                                    detail.order_price}
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                    </div>

                                    <div className="mb-2 text-right">
                                      cost basis <br />
                                      {trade?.excuted_rate ? (
                                        <>
                                          ${formatNumber(trade.excuted_rate)}
                                          <br />
                                          ($
                                          {formatNumber(
                                            trade.excuted_rate *
                                              trade?.order_quantity,
                                          )}
                                          )
                                        </>
                                      ) : (
                                        "-"
                                      )}
                                    </div>
                                  </div>
                                  {/* ✅ Add Stop Loss block */}
                                  {userId ? null : trade?.stop_loss_details &&
                                    trade.stop_loss_details.length > 0 ? (
                                    <>
                                      {trade.stop_loss_details.map(
                                        (sl, idx) => (
                                          <div
                                            key={`stoploss-${idx}`}
                                            className="py-1 flex items-center justify-between"
                                          >
                                            {!isMobileView ? (
                                              <span>
                                                <span className="font-bold mt-2">
                                                  Stop Loss →{" "}
                                                </span>
                                                &nbsp;
                                                {formatDateToEastern(
                                                  sl.updated_at,
                                                  {
                                                    monthFormat: "long",
                                                    includeYear: false,
                                                    includeTime: true,
                                                  },
                                                )}
                                                &nbsp;
                                                {getOrderTypeLabel(
                                                  sl.order_type,
                                                )}{" "}
                                                {sl.order_quantity} shares @{" "}
                                                {sl.order_price
                                                  ? `$${formatNumber(sl.order_price)} ($${formatNumber(
                                                      sl.order_price *
                                                        sl.order_quantity,
                                                    )})`
                                                  : "-"}
                                              </span>
                                            ) : (
                                              <span>
                                                {isMobileView ? (
                                                  <>
                                                    Stop Loss →{" "}
                                                    {getOrderTypeLabel(
                                                      sl.order_type,
                                                    ).toLowerCase()}{" "}
                                                    {sl.order_quantity} @{" "}
                                                    {sl.order_price
                                                      ? `$${formatNumber(sl.order_price)}`
                                                      : "-"}
                                                  </>
                                                ) : (
                                                  <>
                                                    {formatDateToEastern(
                                                      sl.updated_at,
                                                      {
                                                        monthFormat: "short",
                                                        includeYear: false,
                                                        includeTime: true,
                                                      },
                                                    )}{" "}
                                                    {getOrderTypeLabel(
                                                      sl.order_type,
                                                    ).toLowerCase()}{" "}
                                                    {sl.order_quantity} @{" "}
                                                    {sl.order_price
                                                      ? `$${formatNumber(sl.order_price)}`
                                                      : "-"}
                                                  </>
                                                )}
                                              </span>
                                            )}

                                            <div className="flex items-center gap-[34px] sm:gap-11">
                                              <button
                                                onClick={() =>
                                                  handleEditClick(
                                                    sl,
                                                    "stopLoss",
                                                  )
                                                }
                                                className="block text-primaryColor text-sm hover:bg-gray-100 hover:text-gray-900"
                                                role="menuitem"
                                              >
                                                <div className="flex flex-row justify-center items-center">
                                                  <FontAwesomeIcon
                                                    icon={faEdit}
                                                  />
                                                </div>
                                              </button>

                                              <button
                                                onClick={() =>
                                                  handleCancelOrder(sl.id)
                                                }
                                                className="block text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                                role="menuitem"
                                              >
                                                <div className="flex flex-row justify-center items-center">
                                                  <FontAwesomeIcon
                                                    icon={faTrash}
                                                  />
                                                </div>
                                              </button>
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    <div className="font-bold mt-2 flex items-center justify-between">
                                      <span>
                                        Stop Loss →{" "}
                                        <span className="font-normal text-gray-500">
                                          Set a stop loss
                                        </span>
                                      </span>

                                      <button
                                        className="block text-primaryColor text-sm hover:bg-gray-100 hover:text-gray-900"
                                        role="menuitem"
                                        onClick={() =>
                                          handleEditClick(trade, "newStopLoss")
                                        }
                                      >
                                        <div className="flex flex-row justify-center items-center">
                                          <FontAwesomeIcon icon={faWallet} />
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                      ) : (
                        showNonGroups &&
                        !loader && (
                          <>
                            {!loader && holdings.length > 0
                              ? holdings.map((trade, index) => (
                                  <div
                                    key={index}
                                    className="w-full p-3 mt-4 bg-white rounded shadow-md flex items-center"
                                  >
                                    {/* <Link to={ slug ? `/${slug}/buysell/${trade?.stock_symbol}` : `/chartSearch/${trade?.stock_symbol}` } > */}
                                    <div
                                      onClick={() =>
                                        handleAssetClick(trade?.stock_symbol)
                                      }
                                      style={{ cursor: "pointer" }}
                                      className={`max-sm:p-1.5 flex h-[83px] items-center justify-center sm:px-5 rounded-[41px] md:text-[16px] text-[10px] text-center text-white-A700 w-[83px] ${
                                        trade?.order_type === 3 &&
                                        activeButtons === "openTrades"
                                          ? "bg-primaryColor"
                                          : activeButtons === "closedTrades"
                                            ? "bg-primaryColor"
                                            : activeButtons === "holdings"
                                              ? "bg-primaryColor"
                                              : "bg-gray-500"
                                      }`}
                                    >
                                      <span
                                        className={`text-white max-sm:break-all leading-normal ${
                                          isMobileView
                                            ? "text-[11.5px]"
                                            : "text-[13.5px]"
                                        }`}
                                      >
                                        {baseSymbol(trade?.stock_symbol)}
                                      </span>
                                    </div>

                                    <div
                                      className="flex flex-col w-full ml-2"
                                      style={{ gap: "4px" }}
                                    >
                                      <div className="grid grid-cols-2 justify-between">
                                        <div className="flex flex-col">
                                          <p className="text-[12px] text-[#888888] font-inter">
                                            {trade?.order_type === 3
                                              ? "SHORT SELL"
                                              : trade?.order_type === 1
                                                ? "BUY"
                                                : trade?.order_type === 4
                                                  ? "LONG POSITION"
                                                  : trade?.order_type === 5
                                                    ? "BUY"
                                                    : "SELL"}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <p
                                            style={{
                                              color:
                                                trade?.order_type === 3
                                                  ? parseFloat(
                                                      trade?.pnl_amount,
                                                    ) <
                                                    parseFloat(
                                                      trade?.executed_amount,
                                                    )
                                                    ? "#009D00"
                                                    : "#FF4B4B"
                                                  : trade?.order_type === 4
                                                    ? parseFloat(
                                                        trade?.pnl_amount,
                                                      ) <
                                                      parseFloat(
                                                        trade?.executed_amount,
                                                      )
                                                      ? "#FF4B4B"
                                                      : "#009D00"
                                                    : "",
                                            }}
                                            className="text-sm  text-[#000]"
                                          >
                                            {(() => {
                                              const diff = Math.abs(
                                                trade?.pnl_amount -
                                                  trade?.executed_amount,
                                              );

                                              if (diff === 0) return "0";

                                              const isForex =
                                                trade?.asset_type === "forex";

                                              if (diff < 1) {
                                                let rounded = diff.toFixed(4);
                                                if (parseFloat(rounded) === 0) {
                                                  rounded = diff.toFixed(5);
                                                  if (
                                                    parseFloat(rounded) === 0
                                                  ) {
                                                    rounded = diff.toFixed(6);
                                                  }
                                                }
                                                return new Intl.NumberFormat(
                                                  "en-US",
                                                  {
                                                    minimumFractionDigits:
                                                      rounded.split(".")[1]
                                                        .length,
                                                    maximumFractionDigits:
                                                      rounded.split(".")[1]
                                                        .length,
                                                  },
                                                ).format(parseFloat(rounded));
                                              }

                                              // CASE: diff >= 1 → always show 2 decimal places
                                              return new Intl.NumberFormat(
                                                "en-US",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              ).format(diff);
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-1 justify-between">
                                        <div className="flex md:flex-row flex-col justify-end text-right text-sm text-[#888888]">
                                          <div className="flex md:flex-row flex-col justify-end text-right text-sm text-[#888888]">
                                            <span>
                                              {(() => {
                                                const rate = Number(
                                                  trade.excuted_rate,
                                                );

                                                if (rate === 0) return "0";

                                                if (rate >= 1) {
                                                  // Always show 2 decimal places if rate is >= 1
                                                  return new Intl.NumberFormat(
                                                    "en-US",
                                                    {
                                                      minimumFractionDigits: 2,
                                                      maximumFractionDigits: 2,
                                                    },
                                                  ).format(rate);
                                                }

                                                if (
                                                  trade.asset_type === "forex"
                                                ) {
                                                  // For rate < 1 and forex
                                                  let rounded = parseFloat(
                                                    rate.toFixed(4),
                                                  );
                                                  if (rounded === 0) {
                                                    rounded = parseFloat(
                                                      rate.toFixed(5),
                                                    );
                                                    if (rounded === 0) {
                                                      rounded = parseFloat(
                                                        rate.toFixed(6),
                                                      );
                                                    }
                                                  }
                                                  return new Intl.NumberFormat(
                                                    "en-US",
                                                    {
                                                      minimumFractionDigits: 4,
                                                      maximumFractionDigits: 6,
                                                    },
                                                  ).format(rounded);
                                                } else {
                                                  // For non-forex and rate < 1
                                                  return rate % 1 !== 0
                                                    ? new Intl.NumberFormat(
                                                        "en-US",
                                                        {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                        },
                                                      ).format(rate)
                                                    : new Intl.NumberFormat(
                                                        "en-US",
                                                      ).format(rate);
                                                }
                                              })()}{" "}
                                              ($
                                              {formatNumber(
                                                trade?.executed_amount,
                                                trade?.asset_type,
                                              )}
                                              )
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 justify-between items-start mb-1">
                                        <div className="text-xs text-[#888888]">
                                          {activeButtons === "holdings"
                                            ? formatCreatedAtHolding(
                                                trade?.updated_at,
                                              )
                                            : formatCreatedAt(
                                                trade?.updated_at,
                                              )}
                                        </div>
                                        <div className="text-right text-[11px] text-[#888888]">
                                          <span>
                                            (
                                            {formatNumber1(
                                              trade?.order_quantity,
                                            )}
                                            {/* {(trade?.order_quantity).toLocaleString(
                                                "en-US"
                                              )} */}
                                            )
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    {activeButtons === "openTrades" &&
                                    trade?.id !== null &&
                                    isSelfProfile === true ? (
                                      <div style={{ position: "relative" }}>
                                        <button
                                          onClick={() => toggleDropdown(index)}
                                          type="button"
                                        >
                                          <FontAwesomeIcon
                                            icon={faEllipsisV}
                                            className=""
                                          />
                                        </button>
                                        {dropdownStates[index] && (
                                          <div className="absolute top-8 left-0 z-10 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                            <button
                                              onClick={() =>
                                                handleCancelOrder(trade?.id)
                                              }
                                              className="block px-4 py-2 text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                              role="menuitem"
                                            >
                                              <div className="flex flex-row justify-center items-center">
                                                <FontAwesomeIcon
                                                  icon={faTrash}
                                                />
                                              </div>
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleEditClick(trade)
                                              }
                                              className="block px-4 py-2 text-sm text-primaryColor hover:bg-gray-100 hover:text-gray-900"
                                              role="menuitem"
                                            >
                                              <div className="flex flex-row justify-center items-center">
                                                <FontAwesomeIcon
                                                  icon={faEdit}
                                                />
                                              </div>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                    {activeButtons === "holdings" &&
                                    !isIdPresent &&
                                    isSelfProfile === true ? (
                                      <div style={{ position: "relative" }}>
                                        <button
                                          onClick={() =>
                                            toggleButtonDropdown(index)
                                          }
                                          type="button"
                                        >
                                          <FontAwesomeIcon
                                            icon={faEllipsisV}
                                            className=""
                                          />
                                        </button>
                                        {buttonDropdownState === index && (
                                          <div className="absolute top-8 text-green-500 left-[-15px] w-10 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 md:mr-[220px] mr-[20px]">
                                            <button
                                              className="block text-primaryColor px-4 py-2 text-sm hover:bg-gray-100 hover:text-gray-900"
                                              role="menuitem"
                                              onClick={() =>
                                                handleEditClick(trade)
                                              }
                                            >
                                              <div className="flex flex-row justify-center items-center">
                                                <FontAwesomeIcon
                                                  icon={faWallet}
                                                />
                                              </div>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                ))
                              : !loader && (
                                  <div className="text-center font-bold text-black py-4 px-4 mt-4 mb-3">
                                    <p>No Data Found</p>
                                  </div>
                                )}
                          </>
                        )
                      )}
                    </>
                  ) : (
                    ""
                  )}
                  {activeButtons === "holdings" && (
                    <div className="flex flex-col justify-center items-center mt-4">
                      {portfolioMetricsLoading && !freeCoin
                        ? null
                        : freeCoin && (
                            <>
                              <p className="font-bold text-center text-primaryColor max-sm:text-sm">
                                Free Cash:&nbsp;
                                {Number(freeCoin).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </p>
                              <span className="font-bold text-center text-primaryColor mt-2 max-sm:text-sm">
                                Portfolio:&nbsp;
                                {Number(PortfolioCoin).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </>
                          )}
                    </div>
                  )}
                </div>
              </main>
            )}
          </div>
        </div>
        <DeleteOrderModal
          isOpen={openModal}
          handleDeleteCard={handleDeleteOrder}
          handleDeleteCancel={handleDeleteCancel}
        >
          <p className="mb-4 -mt-4">
            Are you sure you want to Cancel this order?
          </p>
        </DeleteOrderModal>
        <MobileEditOrderSheet
          open={!!mobileEditTrade}
          editOrder={mobileEditTrade}
          gameId={game_id}
          onClose={() => setMobileEditTrade(null)}
          onDone={() => {
            setMobileEditTrade(null);
            if (activeButtons === "holdings") {
              fetchOrder(3);
              fetchOrder(1);
            } else {
              fetchOrder(1);
            }
          }}
        />
      </SectionShell>

      {!disableInternalGuide && !embedded && !showChartGuide && guideIcon && (
        <button
          className={`fixed text-white p-0.5 rounded-full shadow-lg hover:opacity-90 z-50 select-none ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          style={{
            bottom: `${guideButtonPosition.bottom}px`,
            right: `${guideButtonPosition.right}px`,
            touchAction: "none",
            backgroundColor: "#35b23c",
          }}
          onClick={() => !hasDraggedSignificantly && setShowChartGuide(true)}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title="Drag to move • Click to show guide (Ctrl+G)"
        >
          <svg width="40" height="40" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}

      {!disableInternalGuide && showChartGuide && (
        <PortfolioGuideSteps setShowGuide={setShowChartGuide} />
      )}
    </>
  );
});

export default NewPortfolioSection;
